import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Rocket, RefreshCw, CheckCircle2, XCircle, ChevronDown, ChevronRight, DollarSign, Download,
} from 'lucide-react';
import { creativeUsageApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';

interface Totals {
  delivered: number;
  launched: number;
  not_launched: number;
  launched_by_ad?: number;
  launched_by_campaign?: number;
  launched_spend?: number;
  actual_cost?: number;
  window_from?: string;
  window_to?: string;
  checked_at?: string;
}

interface BuyerRow {
  media_buyer: string;
  delivered: number;
  launched: number;
  not_launched: number;
  launched_by_ad?: number;
  launched_by_campaign?: number;
  launched_spend: number;
  actual_cost?: number;
}

interface PlatformRow {
  platform: string;
  launched: number;
  launched_spend: number;
}

interface UsageData {
  by_buyer: BuyerRow[];
  totals: Totals;
  by_platform?: PlatformRow[];
}

// 'facebook' → 'Facebook'
function capitalize(s: string): string {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function pct(part: number, whole: number): number {
  const w = Number(whole) || 0;
  if (w <= 0) return 0;
  return Math.round((Number(part) / w) * 100);
}

function money(v: any): string {
  const n = Number(v) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDateTime(d?: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleString();
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString();
}

// Local YYYY-MM-DD (avoids UTC drift from toISOString) for the date-range inputs.
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
}

function todayYmd(): string {
  return ymd(new Date());
}

export function CreativeUsagePage() {
  const { user } = useAuth();
  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const isAdmin = user?.role === 'admin' || additionalRoles.includes('admin');

  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Date range (creative delivery date). Default: last 30 days. Empty = all-time.
  const [from, setFrom] = useState<string>(daysAgo(30));
  const [to, setTo] = useState<string>(todayYmd());

  // Excel export
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // Admin refresh + background-run polling
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [runStatus, setRunStatus] = useState<any | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drill-down state (per-buyer)
  const [expandedBuyer, setExpandedBuyer] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, any[]>>({});
  const [detailTotal, setDetailTotal] = useState<Record<string, number>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [moreLoading, setMoreLoading] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<Record<string, string>>({});
  const DETAIL_PAGE = 100;

  // Tabs: 'buyer' (existing report) | 'unmapped' (delivered-but-never-launched)
  const [activeTab, setActiveTab] = useState<'buyer' | 'unmapped'>('buyer');

  // Unmapped tab data (paginated) + segregation
  const [unmapped, setUnmapped] = useState<any | null>(null);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [unmappedError, setUnmappedError] = useState('');
  const [unmappedOffset, setUnmappedOffset] = useState(0);
  const UNMAPPED_PAGE = 100;

  // Buyer coverage note (loaded once on mount)
  const [coverage, setCoverage] = useState<any | null>(null);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await creativeUsageApi.getUsage(from || undefined, to || undefined);
      setUsage(res.data?.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load creative usage');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  // A new date range invalidates every buyer's cached drill-down; clear so they
  // reload for the new window when re-expanded.
  useEffect(() => {
    setExpandedBuyer(null);
    setDetailCache({});
    setDetailTotal({});
    setDetailError({});
    setUnmappedOffset(0); // date change resets unmapped pagination
  }, [from, to]);

  // Buyer coverage note — load once; don't block render if it fails.
  useEffect(() => {
    creativeUsageApi.getCoverage()
      .then((res) => setCoverage(res.data?.data || null))
      .catch(() => {});
  }, []);

  const loadUnmapped = useCallback(async () => {
    setUnmappedLoading(true);
    setUnmappedError('');
    try {
      const res = await creativeUsageApi.getUnmapped(from || undefined, to || undefined, UNMAPPED_PAGE, unmappedOffset);
      setUnmapped(res.data || null);
    } catch (err: any) {
      setUnmappedError(err?.response?.data?.error || 'Failed to load unmapped creatives');
    } finally {
      setUnmappedLoading(false);
    }
  }, [from, to, unmappedOffset]);

  // Fetch unmapped when its tab is active, the date range changes, or the page moves.
  useEffect(() => {
    if (activeTab === 'unmapped') loadUnmapped();
  }, [activeTab, loadUnmapped]);

  const goTab = (tab: 'buyer' | 'unmapped') => {
    if (tab === 'unmapped') setUnmappedOffset(0); // tab-activate resets pagination
    setActiveTab(tab);
  };

  const setPreset = (days: number | null) => {
    if (days === null) { setFrom(''); setTo(''); }
    else { setFrom(daysAgo(days)); setTo(todayYmd()); }
  };

  // Export the ACTIVE tab: By Buyer → existing report; Unmapped → unmapped report.
  const exportExcel = async () => {
    setExportError('');
    setExporting(true);
    try {
      const isUnmapped = activeTab === 'unmapped';
      const res = isUnmapped
        ? await creativeUsageApi.exportUnmapped(from || undefined, to || undefined)
        : await creativeUsageApi.exportXlsx(from || undefined, to || undefined);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isUnmapped
        ? `Unmapped_Creatives_${from || 'all'}_to_${to || 'today'}.xlsx`
        : `Creative_Usage_${from || 'all'}_to_${to || 'today'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setExportError(err?.response?.data?.error || 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Cleanup any pending poll on unmount
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const startRefresh = async () => {
    setRefreshError('');
    setRefreshing(true);
    try {
      await creativeUsageApi.refresh();
    } catch (err: any) {
      setRefreshError(err?.response?.data?.error || 'Failed to start refresh');
      setRefreshing(false);
      return;
    }

    let sawRunning = false;
    let polls = 0;
    const poll = async () => {
      polls++;
      try {
        const res = await creativeUsageApi.getRunStatus();
        const st = res.data?.data || {};
        setRunStatus(st);
        if (st.running) sawRunning = true;
        // Done when the job is no longer running (guard against the initial
        // not-yet-started race by requiring we saw it running, a finish time,
        // or a few polls elapsed). Hard cap at 40 polls (~10 min).
        const done = (!st.running && (sawRunning || st.finishedAt || polls > 2)) || polls > 40;
        if (done) {
          setRefreshing(false);
          pollRef.current = null;
          await loadUsage();
          return;
        }
      } catch {
        if (polls > 40) { setRefreshing(false); pollRef.current = null; return; }
      }
      pollRef.current = setTimeout(poll, 15000);
    };
    pollRef.current = setTimeout(poll, 15000);
  };

  const toggleBuyer = async (buyer: string) => {
    if (expandedBuyer === buyer) { setExpandedBuyer(null); return; }
    setExpandedBuyer(buyer);
    if (detailCache[buyer]) return; // already loaded
    setDetailLoading(buyer);
    setDetailError(prev => ({ ...prev, [buyer]: '' }));
    try {
      const res = await creativeUsageApi.getBuyerDetail(buyer, DETAIL_PAGE, 0, from || undefined, to || undefined);
      setDetailCache(prev => ({ ...prev, [buyer]: res.data?.data || [] }));
      setDetailTotal(prev => ({ ...prev, [buyer]: res.data?.total ?? (res.data?.data?.length || 0) }));
    } catch (err: any) {
      setDetailError(prev => ({ ...prev, [buyer]: err?.response?.data?.error || 'Failed to load creatives' }));
    } finally {
      setDetailLoading(null);
    }
  };

  // Append the next page of a buyer's creatives (keeps the loaded rows visible while fetching more).
  const loadMoreDetail = async (buyer: string) => {
    const current = detailCache[buyer] || [];
    setMoreLoading(buyer);
    try {
      const res = await creativeUsageApi.getBuyerDetail(buyer, DETAIL_PAGE, current.length, from || undefined, to || undefined);
      setDetailCache(prev => ({ ...prev, [buyer]: [...(prev[buyer] || []), ...(res.data?.data || [])] }));
      setDetailTotal(prev => ({ ...prev, [buyer]: res.data?.total ?? prev[buyer] }));
    } catch (err: any) {
      setDetailError(prev => ({ ...prev, [buyer]: err?.response?.data?.error || 'Failed to load more' }));
    } finally {
      setMoreLoading(null);
    }
  };

  const totals = usage?.totals;
  const rows = [...(usage?.by_buyer || [])].sort(
    (a, b) => Number(b.delivered) - Number(a.delivered),
  );

  // Unmapped derived values (pagination label + Next disable).
  const unmappedTotal = Number(unmapped?.total) || 0;
  const unmappedRows: any[] = unmapped?.data || [];
  const unmappedShown = unmappedRows.length;
  const unmappedRangeLabel = unmappedTotal === 0
    ? '0 of 0'
    : `${unmappedOffset + 1}–${unmappedOffset + unmappedShown} of ${unmappedTotal}`;
  const missingBuyers: any[] = coverage?.missing_buyers || [];

  return (
    <DashboardLayout
      title="Creative Usage"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={exportExcel}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium disabled:opacity-50"
            title="Download this report as an Excel file"
          >
            <Download size={16} className={exporting ? 'animate-pulse' : ''} />
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
          {isAdmin && (
            <button
              onClick={startRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium disabled:opacity-50"
              title="Recompute the mapping (April → today) in the background"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Running…' : 'Refresh mapping'}
            </button>
          )}
          <button
            onClick={loadUsage}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Reload"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          {([['buyer', 'By Buyer'], ['unmapped', 'Unmapped']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => goTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                activeTab === key
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Date range + presets */}
        <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">From</label>
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className="px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">To</label>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className="px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {([['7d', 7], ['30d', 30], ['90d', 90], ['All', null]] as const).map(([label, days]) => {
              const active = days === null
                ? (from === '' && to === '')
                : (from === daysAgo(days) && to === todayYmd());
              return (
                <button
                  key={label}
                  onClick={() => setPreset(days)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${
                    active
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {exportError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{exportError}</p>
          </div>
        )}

        {refreshError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{refreshError}</p>
          </div>
        )}

        {refreshing && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2">
            <RefreshCw size={16} className="animate-spin text-blue-600" />
            <span className="text-sm text-blue-700 dark:text-blue-300">
              Recomputing the mapping in the background…
              {runStatus?.result && (
                <> {' '}({Number(runStatus.result.launched) || 0} launched / {Number(runStatus.result.delivered) || 0} delivered so far)</>
              )}
            </span>
          </div>
        )}

        {activeTab === 'buyer' && (
        <>
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Totals summary cards */}
        {totals && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <span className="text-xs font-medium uppercase">Delivered</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{Number(totals.delivered) || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-green-500 mb-1">
                  <CheckCircle2 size={16} /><span className="text-xs font-medium uppercase">Launched</span>
                </div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{Number(totals.launched) || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  by ad name {Number(totals.launched_by_ad) || 0} · by campaign {Number(totals.launched_by_campaign) || 0}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-red-500 mb-1">
                  <XCircle size={16} /><span className="text-xs font-medium uppercase">Not launched</span>
                </div>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{Number(totals.not_launched) || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-blue-500 mb-1">
                  <Rocket size={16} /><span className="text-xs font-medium uppercase">Launched %</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{pct(totals.launched, totals.delivered)}%</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-amber-500 mb-1">
                  <DollarSign size={16} /><span className="text-xs font-medium uppercase">Actual cost</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{money(totals.actual_cost)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
              Window {fmtDate(totals.window_from)} &rarr; {fmtDate(totals.window_to)}, last checked {fmtDateTime(totals.checked_at)}
            </p>
          </>
        )}

        {/* Buyer-coverage note */}
        {coverage && (
          <p
            className="text-xs text-gray-500 dark:text-gray-400"
            title={missingBuyers.length ? `Only delivered before April: ${missingBuyers.map((b) => b.buyer).join(', ')}` : undefined}
          >
            Buyers shown: {Number(coverage.report_buyers) || 0} of {Number(coverage.total_request_buyers) || 0}.{' '}
            {missingBuyers.length} only delivered before April (outside the ad-data window).
          </p>
        )}

        {/* Launched by platform */}
        {usage?.by_platform && usage.by_platform.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
              Launched by platform
            </div>
            <div className="flex flex-wrap gap-2">
              {usage.by_platform.map((p) => (
                <div
                  key={p.platform}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs whitespace-nowrap"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{capitalize(p.platform)}</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">{Number(p.launched) || 0}</span>
                  <span className="text-gray-500 dark:text-gray-400">{money(p.launched_spend)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* By-buyer table */}
        {loading && !usage ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
            No creative usage data
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                    <th className="px-4 py-3">Media Buyer</th>
                    <th className="px-4 py-3 text-right">Delivered</th>
                    <th className="px-4 py-3 text-right">Launched</th>
                    <th className="px-4 py-3 text-right">By ad</th>
                    <th className="px-4 py-3 text-right">By campaign</th>
                    <th className="px-4 py-3 text-right">Not launched</th>
                    <th className="px-4 py-3 text-right">Launched %</th>
                    <th className="px-4 py-3 text-right">Launched spend</th>
                    <th className="px-4 py-3 text-right">Actual cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row) => {
                    const buyer = row.media_buyer;
                    const isOpen = expandedBuyer === buyer;
                    const detail = detailCache[buyer];
                    return (
                      <React.Fragment key={buyer}>
                        <tr
                          onClick={() => toggleBuyer(buyer)}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                              {isOpen ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                              {buyer || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-white">{Number(row.delivered) || 0}</td>
                          <td className="px-4 py-3 text-right font-semibold text-green-600 dark:text-green-400">{Number(row.launched) || 0}</td>
                          <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{Number(row.launched_by_ad) || 0}</td>
                          <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{Number(row.launched_by_campaign) || 0}</td>
                          <td className="px-4 py-3 text-right font-semibold text-red-600 dark:text-red-400">{Number(row.not_launched) || 0}</td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-white">{pct(row.launched, row.delivered)}%</td>
                          <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{money(row.launched_spend)}</td>
                          <td className="px-4 py-3 text-right text-amber-600 dark:text-amber-400 font-medium">{money(row.actual_cost)}</td>
                        </tr>

                        {isOpen && (
                          <tr className="bg-gray-50/60 dark:bg-gray-900/30">
                            <td colSpan={9} className="px-4 py-3">
                              {detailLoading === buyer ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2">
                                  <RefreshCw size={14} className="animate-spin" /> Loading creatives…
                                </div>
                              ) : detailError[buyer] ? (
                                <p className="text-sm text-red-600 dark:text-red-400 py-2">{detailError[buyer]}</p>
                              ) : !detail || detail.length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No creatives for this buyer</p>
                              ) : (
                                <div className="space-y-1">
                                  {detail.map((c: any) => (
                                    <div
                                      key={c.id}
                                      className="flex items-center gap-3 px-3 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
                                    >
                                      {c.launched ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 whitespace-nowrap">
                                          <CheckCircle2 size={12} /> Launched
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 whitespace-nowrap">
                                          <XCircle size={12} /> Not launched
                                        </span>
                                      )}
                                      {c.launched && c.matched_on && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap">
                                          {c.matched_on === 'campaign' ? 'campaign name' : 'ad name'}
                                        </span>
                                      )}
                                      {c.launched && c.platform && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400 whitespace-nowrap">
                                          {capitalize(c.platform)}
                                        </span>
                                      )}
                                      <span className="text-sm text-gray-900 dark:text-white truncate flex-1 min-w-0" title={c.filename}>
                                        {c.filename || '—'}
                                      </span>
                                      {c.ad_name && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[240px]" title={c.ad_name}>
                                          {c.ad_name}
                                        </span>
                                      )}
                                      {(c.matched_on === 'campaign' || c.campaign_name) && c.campaign_name && (
                                        <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[240px]" title={c.campaign_name}>
                                          {c.campaign_name}
                                        </span>
                                      )}
                                      <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-0.5 whitespace-nowrap">
                                        <DollarSign size={11} className="text-gray-400" />
                                        {(Number(c.spend) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                      {c.actual_cost != null && (
                                        <span className="text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap" title="Actual production cost">
                                          cost {money(c.actual_cost)}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                  {detailTotal[buyer] != null && (
                                    <div className="flex items-center justify-between pt-1">
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Showing {detail.length} of {detailTotal[buyer]}
                                      </span>
                                      {detail.length < detailTotal[buyer] && (
                                        <button
                                          onClick={() => loadMoreDetail(buyer)}
                                          disabled={moreLoading === buyer}
                                          className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                                        >
                                          {moreLoading === buyer ? 'Loading…' : `Load more (${DETAIL_PAGE})`}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}

        {/* Unmapped tab */}
        {activeTab === 'unmapped' && (
        <>
          {/* Summary line */}
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Total unmapped: <span className="font-semibold text-gray-900 dark:text-white">{unmappedTotal}</span>{' '}
            (delivered but never launched) — range {from || 'all'} &rarr; {to || 'today'}
          </p>

          {/* Segregation: by editor / by buyer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                By editor
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                {(unmapped?.by_editor || []).length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">—</div>
                ) : (
                  (unmapped.by_editor as any[]).map((e, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-gray-900 dark:text-white truncate">{e.editor || '—'}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{Number(e.unmapped) || 0}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                By buyer
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                {(unmapped?.by_buyer || []).length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">—</div>
                ) : (
                  (unmapped.by_buyer as any[]).map((b, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-gray-900 dark:text-white truncate">{b.media_buyer || '—'}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{Number(b.unmapped) || 0}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Main table */}
          {unmappedError ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400">{unmappedError}</p>
            </div>
          ) : unmappedLoading && !unmapped ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="animate-spin text-blue-600" size={32} />
            </div>
          ) : unmappedRows.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
              No unmapped creatives in this range
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      <th className="px-4 py-3">File</th>
                      <th className="px-4 py-3">Editor</th>
                      <th className="px-4 py-3">Media Buyer</th>
                      <th className="px-4 py-3">Request</th>
                      <th className="px-4 py-3">Delivered</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {unmappedRows.map((r, i) => (
                      <tr key={r.request_id ? `${r.request_id}-${i}` : i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                        <td className="px-4 py-3 text-gray-900 dark:text-white truncate max-w-[280px]" title={r.filename}>{r.filename || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{r.editor || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{r.media_buyer || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 truncate max-w-[280px]" title={r.request_title}>{r.request_title || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtDate(r.delivered_day)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                <span className="text-xs text-gray-500 dark:text-gray-400">{unmappedRangeLabel}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setUnmappedOffset((o) => Math.max(0, o - UNMAPPED_PAGE))}
                    disabled={unmappedOffset === 0 || unmappedLoading}
                    className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setUnmappedOffset((o) => o + UNMAPPED_PAGE)}
                    disabled={unmappedOffset + UNMAPPED_PAGE >= unmappedTotal || unmappedLoading}
                    className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
        )}
      </div>
    </DashboardLayout>
  );
}
